import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import logo from "@/assets/valiosia-logo.png";

// Boot splash for the authenticated app (Epic 11.4) — "a constelação que acende
// a marca". A layered dark scene (starfield + slow rose bloom) over which the
// official logotype rises (fade + lift → organic glow pulse), closed by a 1px
// rose shimmer. All CSS keyframes (transform/opacity/filter only, 60fps), the
// whole intro lands under ~1.8s, and it degrades to a static fade under
// `prefers-reduced-motion`. Contract is unchanged: shows once per session,
// fades out (no layout shift) once the first data arrives.
const SESSION_KEY = "valiosia_splash_seen";

export function shouldShowSplash() {
  try {
    return !sessionStorage.getItem(SESSION_KEY);
  } catch {
    return false;
  }
}

export default function AppSplash({ ready = false, minMs = 1400, onDone }) {
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* private mode */ }
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const wait = Math.max(0, minMs - (Date.now() - mountedAt.current));
    const t = setTimeout(() => setLeaving(true), wait);
    return () => clearTimeout(t);
  }, [ready, minMs]);

  if (gone || typeof document === "undefined") return null;

  return createPortal(
    <div
      data-testid="app-splash"
      aria-hidden="true"
      onTransitionEnd={(e) => {
        if (leaving && e.propertyName === "opacity") { setGone(true); onDone?.(); }
      }}
      className={`splash-root fixed inset-0 z-[70] flex flex-col items-center justify-center overflow-hidden ${
        leaving ? "splash-leaving" : ""
      }`}
    >
      {/* Depth layers: fine starfield + slow-drifting rose bloom */}
      <div className="starfield absolute inset-0" />
      <div className="splash-bloom" />

      <div className="relative flex flex-col items-center">
        <img
          src={logo}
          alt="ValiosIA"
          draggable="false"
          className="splash-logo w-64 max-w-[72vw] select-none"
        />

        <div className="splash-track mt-10" />
      </div>
    </div>,
    document.body,
  );
}
